import React, { useEffect } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import SingleSelectField from '@grnhse/seedling/lib/birch/components/forms/fields/single_select_field';
import { SimpleSingleDropdown } from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import { PaginatedMultiDropdown } from 'candidate_profile_redesign/shared/form/Fields';
import CheckboxField from '@grnhse/seedling/lib/birch/components/forms/fields/checkbox_field';
import TextField from '@grnhse/seedling/lib/birch/components/forms/fields/text_field';
import { Label } from '@grnhse/seedling/lib/birch/typography';
import TinyMCE from 'tinymce/localized_editor';
import TinyMCEConfig from 'tinymce/config';
import { withLocalePrefix } from 'shared/utils/translation';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { useEmailTemplate } from 'talent_matching/hooks/use_email_template';
import { TemplateOption } from 'candidate_profile_redesign/shared/types';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import BulkEmailCallout from './bulk_email_callout';
import UnverifiedDomainCallout from './unverified_domain_callout';

const t = withLocalePrefix('talent_matching.bulk_actions.send_email');

type Props = {
  totalSelected: number;
  templateOptions: TemplateOption[];
  getCcOptionsPath: string;
  fromOptions: Array<Item & { domain: string; domain_unverified: boolean }>;
  displayDomainWarning: boolean;
};

const EmailSection = ({
  totalSelected,
  templateOptions,
  getCcOptionsPath,
  fromOptions,
  displayDomainWarning,
}: Props) => {
  const { watch, setValue } = useFormContext();
  const [htmlBody, template, from] = watch(['html_body', 'template', 'from']);

  const { data: emailTemplate, isFetching } = useEmailTemplate(template?.value);

  const displayCallout =
    displayDomainWarning && from?.domain_unverified === true;

  useEffect(() => {
    if (emailTemplate) {
      const fromValue = fromOptions.find(
        (option) => option.value === emailTemplate.from
      );
      setValue('from', fromValue);
      setValue('subject', emailTemplate.subject);
      setValue('html_body', emailTemplate.html_body);
      setValue(
        'cc',
        emailTemplate.cc.map((email: string) => ({
          label: email,
          value: email,
        }))
      );
      setValue('cc_recruiter', emailTemplate.cc_recruiter);
      setValue('cc_coordinator', emailTemplate.cc_coordinator);
      setValue('include_self', false);
    } else {
      setValue('from', '');
      setValue('subject', '');
      setValue('html_body', '');
      setValue('cc', []);
      setValue('cc_recruiter', false);
      setValue('cc_coordinator', false);
      setValue('include_self', false);
    }
  }, [emailTemplate, setValue, fromOptions]);

  return (
    <Stack
      spacing={Tokens.spacing['2']}
      sx={{ marginBottom: Tokens.spacing['1.5'] }}
    >
      <SingleSelectField
        name="template"
        label={t('template')}
        items={templateOptions || []}
        disabled={isFetching}
      />
      <BulkEmailCallout />
      <SingleSelectField
        name="from"
        label={t('from')}
        items={fromOptions}
        clearable={false}
        disabled={isFetching}
        required
        rules={{ required: t('from_required') }}
      />
      {displayCallout && (
        <UnverifiedDomainCallout domain={from?.domain || ''} />
      )}
      <SimpleSingleDropdown
        items={[]}
        onSelectedItemChange={() => null}
        selectedItem={{
          value: t('count_candidates', {
            count: totalSelected,
          }),
          label: t('count_candidates', {
            count: totalSelected,
          }),
        }}
        label={t('to')}
        readOnly={true}
        disabled={isFetching}
      />
      <PaginatedMultiDropdown
        getOptionsUrl={getCcOptionsPath}
        name="cc"
        label={t('cc')}
        disabled={isFetching}
      />
      <Stack direction="row" spacing={Tokens.spacing['2']}>
        <CheckboxField
          name="cc_recruiter"
          label={t('cc_recruiter')}
          value="cc_recruiter"
          disabled={isFetching}
        />
        <CheckboxField
          name="cc_coordinator"
          label={t('cc_coordinator')}
          value="cc_coordinator"
          disabled={isFetching}
        />
        <CheckboxField
          name="include_self"
          label={t('include_self')}
          value="include_self"
          disabled={isFetching}
        />
      </Stack>
      <TextField
        name="subject"
        label={t('subject')}
        rules={{ required: t('subject_required') }}
        disabled={isFetching}
      />
      <Label>{t('body')}</Label>
      <TinyMCE
        init={{
          ...TinyMCEConfig.Email,
          selector: undefined,
        }}
        onEditorChange={(content) => {
          setValue('html_body', content);
        }}
        value={htmlBody}
        disabled={isFetching}
      />
    </Stack>
  );
};

export default EmailSection;
